import React from 'react';
import { Button } from '../../../../components/button';
import { RoccoLink } from '../../../../components/link/RoccoLink';
import { ReactComponent as APLogoWhite } from '../../../../icons/svg/logo-new-white.svg';
import { BackgroundVideo } from './BackgroundVideo';
import type { FooterVideoData } from './types';

interface GlobeSectionProps {
    onSignUpClick?: () => void;
    /** Optional video data for globe animation */
    videoData?: FooterVideoData | null;
    /** Fallback background image URL when video is absent */
    fallbackImageSrc?: string;
    /** Whether the user is logged in (hides sign up button) */
    isLoggedIn?: boolean;
}

/**
 * Globe section for the new footer design.
 * Displays a slogan with ArchiPro logo.
 * Can accept a background video to be displayed behind the content.
 */
export const GlobeSection: React.FC<GlobeSectionProps> = ({
    onSignUpClick,
    videoData,
    fallbackImageSrc,
    isLoggedIn,
}) => {
    return (
        <section className="relative mx-12 mb-10 overflow-hidden border-t border-white/20 bg-[#212121] py-24">
            <div className="relative">
                {/* Content container with globe behind */}
                <div className="relative flex min-h-[28rem] flex-col items-center justify-center text-center">
                    {/* Globe - positioned behind text, centered */}
                    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <div className="relative h-[23rem] w-[23rem] [-webkit-clip-path:circle(48%)] [clip-path:circle(48%)] md:h-[45rem] md:w-[45rem]">
                            {videoData ? (
                                <>
                                    <BackgroundVideo
                                        videoData={videoData}
                                        className="scale-[1.02]"
                                    />
                                    {/* Overlay to transform black video background to #212121 */}
                                    <div className="pointer-events-none absolute inset-0 h-full w-full bg-[#212121] mix-blend-lighten" />
                                </>
                            ) : fallbackImageSrc ? (
                                <>
                                    <div
                                        className="h-full w-full scale-[1.02] bg-cover bg-center bg-no-repeat"
                                        style={{
                                            backgroundImage: `url(${fallbackImageSrc})`,
                                        }}
                                        aria-hidden="true"
                                    />
                                    {/* Overlay to blend dark image background with #212121 footer */}
                                    <div className="pointer-events-none absolute inset-0 h-full w-full bg-[#212121] mix-blend-lighten" />
                                </>
                            ) : (
                                /* CSS gradient fallback */
                                <div className="from-amber-600/20 via-amber-700/15 to-amber-900/10 h-full w-full bg-gradient-to-b opacity-80">
                                    <div className="h-full w-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,200,100,0.3)_0%,transparent_50%)] opacity-60" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Text content - centered vertically on top of globe */}
                    <div className="relative z-10 flex flex-col items-center">
                        {/* Main heading - 80px per Figma */}
                        <h2 className="mb-8 mt-14 text-[2.5rem] font-normal leading-[98.75%] tracking-[-1.6px] text-white md:text-[3.5rem] lg:text-[5rem]">
                            The world of architecture,
                            <br />
                            curated for you
                        </h2>

                        {/* Subtext + Sign Up button - hidden when logged in */}
                        {!isLoggedIn && (
                            <>
                                <p className="mb-8 text-[1.25rem] tracking-[-0.6px] text-white">
                                    Join the movement
                                </p>

                                <Button
                                    color="white"
                                    size="default"
                                    textSize="small"
                                    weight="normal"
                                    onClick={onSignUpClick}
                                    className="h-9.5 w-39 shadow-[0px_4px_24px_0px_rgba(0,0,0,0.34)]"
                                >
                                    Sign Up Now
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* ArchiPro logo - absolute right, ~5% from edge per Figma */}
                <div className="absolute right-[3%] top-[68%] hidden -translate-y-1/2 lg:block">
                    <RoccoLink to="/" prefetch="intent" aria-label="Home">
                        <APLogoWhite className="h-[9.5rem] w-auto" />
                    </RoccoLink>
                </div>
            </div>
        </section>
    );
};
